import { Box, Link } from '@mui/material'
import { Fragment, ReactNode } from 'react'

// Just enough markdown for chat replies: paragraphs, **bold**, "- " bullet
// lists and [links](https://…). Everything renders as React text, so model
// output can never inject markup; only http(s) and mailto links are linked.

const INLINE = /\*\*(.+?)\*\*|\[([^\]]+)\]\(([^)\s]+)\)|(https?:\/\/[^\s)]+)/g
const SAFE_HREF = /^(https?:|mailto:)/i

function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const nodes: ReactNode[] = []
  let last = 0
  let match: RegExpExecArray | null
  INLINE.lastIndex = 0
  while ((match = INLINE.exec(text)) !== null) {
    if (match.index > last) nodes.push(text.slice(last, match.index))
    const key = `${keyPrefix}-${match.index}`
    const [whole, bold, linkText, linkHref, bareUrl] = match
    const href = linkHref ?? bareUrl
    if (bold !== undefined) {
      nodes.push(<strong key={key}>{bold}</strong>)
    } else if (href && SAFE_HREF.test(href)) {
      nodes.push(
        <Link
          key={key}
          href={href}
          target='_blank'
          rel='noopener noreferrer'
          color='inherit'
          underline='always'
        >
          {linkText ?? href}
        </Link>
      )
    } else {
      nodes.push(whole)
    }
    last = match.index + whole.length
  }
  if (last < text.length) nodes.push(text.slice(last))
  return nodes
}

const BULLET = /^\s*[-*•]\s+/

export default function MarkdownLite({ text }: Readonly<{ text: string }>) {
  const blocks = text.replace(/\r\n/g, '\n').split(/\n\s*\n/)

  return (
    <>
      {blocks.map((block, blockIndex) => {
        const lines = block.split('\n').filter(line => line.trim() !== '')
        if (lines.length === 0) return null
        const key = `b${blockIndex}`

        // A block may open with a lead-in line followed by bullets.
        const firstBullet = lines.findIndex(line => BULLET.test(line))
        const bulletsOnly =
          firstBullet >= 0 &&
          lines.slice(firstBullet).every(line => BULLET.test(line))
        const lead = bulletsOnly ? lines.slice(0, firstBullet) : lines

        return (
          <Fragment key={key}>
            {lead.length > 0 && (
              <Box component='p' sx={{ m: 0, '& + *': { mt: 1 } }}>
                {lead.map((line, i) => (
                  <Fragment key={`${key}-l${i}`}>
                    {i > 0 && <br />}
                    {renderInline(line, `${key}-l${i}`)}
                  </Fragment>
                ))}
              </Box>
            )}
            {bulletsOnly && (
              <Box component='ul' sx={{ m: 0, pl: 2.5, '& + *': { mt: 1 } }}>
                {lines.slice(firstBullet).map((line, i) => (
                  <li key={`${key}-i${i}`}>
                    {renderInline(line.replace(BULLET, ''), `${key}-i${i}`)}
                  </li>
                ))}
              </Box>
            )}
          </Fragment>
        )
      })}
    </>
  )
}
